import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, API, downloadPdf as downloadPdfFile } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Save, Plus, Trash2, Download, MessageCircle, UserPlus, FileText, Upload, Camera, Paperclip, ExternalLink, Folder } from "lucide-react";
import { CONCEPTS, money, whatsappLink } from "@/lib/cobranza";
import { PLAN_KEYS, PLAN_LABELS, planAmount } from "@/lib/plans";
import { useAuth } from "@/lib/auth";
import { RecargosManager } from "@/components/app/RecargosManager";

const FILE_CATEGORIES = ["General", "Médicos", "Académicos", "Inscripción", "Justificantes", "Otros"];

function Card({ children, accent = "bg-navy", testid }) {
  return (
    <div className="bg-white border border-nardo-200 rounded-md p-6 relative" data-testid={testid}>
      <div className={`absolute top-0 left-0 h-[2px] w-16 ${accent}`} />
      {children}
    </div>
  );
}

export default function ExpedienteAlumno() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [student, setStudent] = useState(null);
  const [payments, setPayments] = useState([]);
  const [expFiles, setExpFiles] = useState([]);
  const [incidents, setIncidents] = useState([]);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [fileCategory, setFileCategory] = useState("General");
  const expFileRef = React.useRef(null);
  const expCameraRef = React.useRef(null);

  // billing form
  const [tutors, setTutors] = useState([{ name: "", whatsapp: "", relation: "" }]);
  const [address, setAddress] = useState("");
  const [scholarship, setScholarship] = useState("");
  const [scholarshipName, setScholarshipName] = useState("");
  const [monthlyFee, setMonthlyFee] = useState("");
  const [plan, setPlan] = useState("");
  const [enrollmentFee, setEnrollmentFee] = useState("");
  const [savingBilling, setSavingBilling] = useState(false);

  // quick pay form
  const [concept, setConcept] = useState("Colegiatura");
  const [period, setPeriod] = useState(new Date().toISOString().slice(0, 7));
  const [amount, setAmount] = useState("");
  const [discount, setDiscount] = useState("");
  const [applyScholarship, setApplyScholarship] = useState(true);
  const [method, setMethod] = useState("Efectivo");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const [sList, pays, fls] = await Promise.all([
        api.get("/students", { params: { kind: "student" } }),
        api.get("/payments", { params: { student_id: id } }),
        api.get(`/students/${id}/files`),
      ]);
      const s = sList.data.find((x) => x.id === id);
      setStudent(s || null);
      setPayments(pays.data);
      setExpFiles(fls.data || []);
      api.get("/direccion/incidents", { params: { student_id: id } }).then((r) => setIncidents(r.data.incidents || [])).catch(() => {});
      const b = s?.billing || {};
      setTutors(b.tutors?.length ? b.tutors.map((t) => ({ name: t.name || "", whatsapp: t.whatsapp || "", relation: t.relation || "" })) : [{ name: "", whatsapp: "", relation: "" }]);
      setAddress(b.address || "");
      setScholarship(b.scholarship_pct ? String(b.scholarship_pct) : "");
      setScholarshipName(b.scholarship_name || "");
      setMonthlyFee(b.monthly_fee ? String(b.monthly_fee) : "");
      setPlan(b.plan || "");
      setEnrollmentFee(b.enrollment_fee ? String(b.enrollment_fee) : "");
    } catch (e) { console.error(e); }
  };
  useEffect(() => { load(); }, [id]); // eslint-disable-line

  const billing = student?.billing || {};

  const saveBilling = async (e) => {
    e.preventDefault();
    setSavingBilling(true);
    try {
      await api.put(`/students/${id}/billing`, {
        tutors, address, plan,
        scholarship_pct: Number(scholarship) || 0,
        scholarship_name: scholarshipName || "",
        monthly_fee: Number(monthlyFee) || 0,
        enrollment_fee: Number(enrollmentFee) || 0,
      });
      toast.success("Expediente guardado");
      load();
    } catch { toast.error("Error al guardar"); }
    finally { setSavingBilling(false); }
  };

  useEffect(() => {
    const fee = concept === "Colegiatura" ? monthlyFee : concept === "Inscripción" ? enrollmentFee : "";
    setAmount(fee ? String(fee) : "");
  }, [concept, monthlyFee, enrollmentFee]); // eslint-disable-line

  const preview = useMemo(() => {
    const amt = Math.max(0, Number(amount) || 0);
    const disc = Math.max(0, Number(discount) || 0);
    const sch = applyScholarship && concept === "Colegiatura" ? (amt * (Number(scholarship) || 0)) / 100 : 0;
    return { net: Math.max(0, amt - disc - sch) };
  }, [amount, discount, applyScholarship, concept, scholarship]);

  const registerPayment = async (e) => {
    e.preventDefault();
    if (!(Number(amount) > 0)) { toast.error("Importe inválido"); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/payments", {
        student_id: id, concept, period: concept === "Colegiatura" ? period : null,
        amount: Number(amount), discount: Number(discount) || 0, apply_scholarship: applyScholarship, method,
      });
      toast.success(`Pago registrado · ${data.folio} · ${money(data.net_amount)}`);
      setAmount(""); setDiscount("");
      load();
      const link = whatsappLink(data, billing.tutors?.[0]?.whatsapp, user?.school_name);
      if (link) window.open(link, "_blank");
    } catch (err) { toast.error(err?.response?.data?.detail || "Error"); }
    finally { setSaving(false); }
  };

  const downloadReceipt = async (p) => {
    try {
      await downloadPdfFile(`/payments/${p.id}/receipt`, { filename: `comprobante_${p.folio}.pdf` });
    } catch { toast.error("Error al descargar"); }
  };
  const sendWa = (p) => {
    const link = whatsappLink(p, billing.tutors?.[0]?.whatsapp, user?.school_name);
    if (!link) { toast.error("Agrega el WhatsApp del tutor en el expediente"); return; }
    window.open(link, "_blank");
  };

  const [stmtBusy, setStmtBusy] = useState(false);
  const downloadStatement = async () => {
    setStmtBusy(true);
    try {
      const month = new Date().toISOString().slice(0, 7);
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      await downloadPdfFile(`/students/${id}/payment-statement-pdf`, { params: { month, tz }, filename: `expediente_pago_${student.full_name.replace(/\s+/g, "_")}_${month}.pdf` });
      toast.success("Expediente de pago generado");
    } catch { toast.error("No se pudo generar el PDF"); }
    finally { setStmtBusy(false); }
  };

  const byMonth = useMemo(() => {
    const groups = {};
    payments.forEach((p) => {
      const key = p.period || (p.created_at || "").slice(0, 7);
      (groups[key] = groups[key] || []).push(p);
    });
    return Object.entries(groups).sort((a, b) => b[0].localeCompare(a[0]));
  }, [payments]);

  const total = useMemo(() => payments.reduce((s, p) => s + (p.net_amount || 0), 0), [payments]);
  const filesByCategory = useMemo(() => {
    const groups = {};
    for (const f of expFiles) {
      const cat = f.category || "General";
      (groups[cat] = groups[cat] || []).push(f);
    }
    const order = FILE_CATEGORIES;
    return Object.entries(groups).sort((a, b) => {
      const ia = order.indexOf(a[0]); const ib = order.indexOf(b[0]);
      return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib);
    });
  }, [expFiles]);

  const uploadExpFiles = async (list) => {
    const arr = Array.from(list || []);
    if (!arr.length) return;
    setUploadingFile(true);
    try {
      for (const f of arr) {
        const fd = new FormData();
        fd.append("file", f);
        fd.append("title", f.name);
        fd.append("source", "general");
        fd.append("category", fileCategory || "General");
        await api.post(`/students/${id}/files`, fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 120000 });
      }
      toast.success(`${arr.length} archivo(s) agregado(s) a "${fileCategory}"`);
      if (expFileRef.current) expFileRef.current.value = "";
      if (expCameraRef.current) expCameraRef.current.value = "";
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo subir el archivo"); }
    finally { setUploadingFile(false); }
  };
  const deleteExpFile = async (fid) => {
    try { await api.delete(`/students/${id}/files/${fid}`); toast.success("Archivo eliminado"); load(); }
    catch { toast.error("Error"); }
  };

  if (!student) return <div className="py-16 text-center text-nardo-500 text-sm">Cargando expediente…</div>;

  return (
    <div className="max-w-7xl mx-auto" data-testid="expediente-page">
      <button onClick={() => navigate("/students")} className="inline-flex items-center gap-2 text-sm text-nardo-500 hover:text-navy mb-6"><ArrowLeft className="w-4 h-4" /> Volver a Alumnos</button>
      <div className="mb-8">
        <div className="text-[11px] uppercase tracking-[0.3em] text-navy-light mb-2">Expediente del alumno</div>
        <h1 className="font-display font-extrabold text-3xl text-navy">{student.full_name}</h1>
        {(() => { const t = tutors.find((x) => (x.name || "").trim()); if (!t) return null; const rel = (t.relation || "").trim(); return <p className="text-sm text-nardo-500 mt-1" data-testid="exp-tutor-label">Tutor: {rel ? `${t.name} (${rel.toLowerCase()})` : t.name}</p>; })()}
        <p className="text-sm text-nardo-500 mt-1">{student.grade || "—"} · Total pagado: <span className="text-emerald-700 font-semibold">{money(total)}</span></p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Datos / Plan de pago */}
        <Card testid="expediente-billing-card">
          <h3 className="font-display font-semibold text-lg text-navy mb-4">Datos del alumno y plan de pago</h3>
          <form onSubmit={saveBilling} className="space-y-4">
            {tutors.map((t, i) => (
              <div key={i} className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <input data-testid={`tutor-name-${i}`} value={t.name} onChange={(e) => setTutors(tutors.map((x, j) => j === i ? { ...x, name: e.target.value } : x))} placeholder={`Tutor ${i + 1}`} className="border-b-2 border-nardo-200 bg-transparent py-2 outline-none focus:border-navy text-navy text-sm" />
                <select data-testid={`tutor-relation-${i}`} value={t.relation || ""} onChange={(e) => setTutors(tutors.map((x, j) => j === i ? { ...x, relation: e.target.value } : x))} className="border-b-2 border-nardo-200 bg-transparent py-2 outline-none focus:border-navy text-navy text-sm">
                  <option value="">Parentesco…</option>
                  {["Mamá", "Papá", "Tutor(a)", "Abuelo(a)", "Otro"].map((r) => <option key={r} value={r}>{r}</option>)}
                </select>
                <input data-testid={`tutor-whatsapp-${i}`} value={t.whatsapp} onChange={(e) => setTutors(tutors.map((x, j) => j === i ? { ...x, whatsapp: e.target.value } : x))} placeholder="WhatsApp (ej. 5215551234567)" className="border-b-2 border-nardo-200 bg-transparent py-2 outline-none focus:border-navy text-navy text-sm" />
              </div>
            ))}
            <button type="button" onClick={() => setTutors([...tutors, { name: "", whatsapp: "", relation: "" }])} className="text-xs text-navy hover:text-navy-light inline-flex items-center gap-1" data-testid="add-tutor"><UserPlus className="w-3.5 h-3.5" /> Agregar tutor</button>
            <input data-testid="exp-address" value={address} onChange={(e) => setAddress(e.target.value)} placeholder="Dirección" className="w-full border-b-2 border-nardo-200 bg-transparent py-2 outline-none focus:border-navy text-navy text-sm" />
            <div>
              <label className="text-[10px] uppercase tracking-[0.15em] text-nardo-500">Plan de pago</label>
              <select data-testid="exp-plan" value={plan} disabled className="w-full border-b-2 border-nardo-200 bg-transparent py-2 outline-none text-nardo-500 disabled:text-nardo-500 cursor-not-allowed">
                <option value="">Sin plan (colegiatura manual)</option>
                {PLAN_KEYS.map((k) => { const amt = planAmount(user, student?.level, k, student?.grade); return (
                  <option key={k} value={k}>{PLAN_LABELS[k]}{amt > 0 ? ` — $${amt.toLocaleString("es-MX")}` : ""}</option>
                ); })}
              </select>
              <p className="text-[11px] text-amber-600 mt-1">El plan, colegiatura, inscripción y beca se editan al registrar al alumno. Aquí son solo informativos.</p>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div><label className="text-[10px] uppercase tracking-[0.15em] text-nardo-500">Colegiatura</label><input data-testid="exp-monthly" type="number" value={monthlyFee} disabled className="w-full border-b-2 border-nardo-200 bg-transparent py-2 outline-none text-nardo-500 cursor-not-allowed" /></div>
              <div><label className="text-[10px] uppercase tracking-[0.15em] text-nardo-500">Inscripción</label><input data-testid="exp-enrollment" type="number" value={enrollmentFee} disabled className="w-full border-b-2 border-nardo-200 bg-transparent py-2 outline-none text-nardo-500 cursor-not-allowed" /></div>
              <div><label className="text-[10px] uppercase tracking-[0.15em] text-nardo-500">Beca %</label><input data-testid="exp-scholarship" type="number" value={scholarship} disabled className="w-full border-b-2 border-nardo-200 bg-transparent py-2 outline-none text-nardo-500 cursor-not-allowed" /></div>
            </div>
            {scholarshipName && <p className="text-[11px] text-nardo-500" data-testid="exp-scholarship-name">Beca: <b className="text-navy">{scholarshipName}</b>{scholarship ? ` (${scholarship}%)` : ""}</p>}
            {(Number(scholarship) > 0 && Number(monthlyFee) > 0) && (
              <p className="text-[12px] text-emerald-700 font-medium" data-testid="exp-effective-fee">
                Colegiatura con beca ({Number(scholarship)}%{plan ? ` sobre plan ${plan === "basico" ? "Básico" : plan === "medio" ? "Medio tiempo" : "Tiempo completo"}` : ""}): ${(Number(monthlyFee) - Number(monthlyFee) * Number(scholarship) / 100).toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} / mes
              </p>
            )}
            <button data-testid="save-billing" type="submit" disabled={savingBilling} className="inline-flex items-center gap-2 bg-navy hover:bg-navy-light text-white px-5 py-2.5 rounded-md text-sm font-medium disabled:opacity-50"><Save className="w-4 h-4" /> {savingBilling ? "Guardando…" : "Guardar expediente"}</button>
          </form>

          <div className="mt-6 pt-5 border-t border-nardo-100">
            <h4 className="font-display font-semibold text-navy mb-3">Registrar pago</h4>
            <form onSubmit={registerPayment} className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <select data-testid="exp-pay-concept" value={concept} onChange={(e) => setConcept(e.target.value)} className="border border-nardo-200 rounded-md p-2 text-sm text-navy bg-white outline-none focus:border-navy">
                  {CONCEPTS.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
                {concept === "Colegiatura" ? <input data-testid="exp-pay-period" type="month" value={period} onChange={(e) => setPeriod(e.target.value)} className="border-b-2 border-nardo-200 bg-transparent py-2 outline-none focus:border-navy text-navy" /> : <div />}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <input data-testid="exp-pay-amount" type="number" min="0" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="Importe" className="border-b-2 border-nardo-200 bg-transparent py-2 outline-none focus:border-navy text-navy" />
                <input data-testid="exp-pay-discount" type="number" min="0" step="0.01" value={discount} onChange={(e) => setDiscount(e.target.value)} placeholder="Descuento" className="border-b-2 border-nardo-200 bg-transparent py-2 outline-none focus:border-navy text-navy" />
              </div>
              {concept === "Colegiatura" && Number(scholarship) > 0 && (
                <label className="flex items-center gap-2 text-sm text-navy"><input type="checkbox" checked={applyScholarship} onChange={(e) => setApplyScholarship(e.target.checked)} /> Aplicar beca ({scholarship}%)</label>
              )}
              <div className="flex items-center justify-between text-sm">
                <span className="text-nardo-600">Total a pagar:</span>
                <span className="text-navy font-bold" data-testid="exp-pay-net">{money(preview.net)}</span>
              </div>
              <button data-testid="exp-pay-submit" type="submit" disabled={saving} className="inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white px-5 py-2.5 rounded-md text-sm font-medium disabled:opacity-50"><Plus className="w-4 h-4" /> {saving ? "Registrando…" : "Registrar y enviar comprobante"}</button>
            </form>
          </div>
        </Card>

        {/* Recargos (Dirección) */}
        <RecargosManager
          studentId={id}
          canManage={!["staff", "docente", "coordinacion"].includes(user?.role)}
          onChanged={load}
        />

        {/* Pagos por mes */}
        <Card accent="bg-emerald-500" testid="expediente-payments-card">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-display font-semibold text-lg text-navy">Pagos por mes</h3>
            <button onClick={downloadStatement} disabled={stmtBusy} className="inline-flex items-center gap-2 border border-navy/30 text-navy hover:bg-navy hover:text-white px-3 py-1.5 rounded-md text-xs font-medium transition-colors disabled:opacity-50" data-testid="download-statement">
              <FileText className="w-3.5 h-3.5" /> {stmtBusy ? "Generando…" : "Expediente PDF (mes)"}
            </button>
          </div>
          {byMonth.length === 0 ? <p className="text-sm text-nardo-500">Aún no hay pagos registrados.</p> : (
            <div className="space-y-4 max-h-[34rem] overflow-y-auto pr-1">
              {byMonth.map(([m, list]) => (
                <div key={m}>
                  <div className="text-[10px] uppercase tracking-[0.2em] text-nardo-400 mb-1">{m}</div>
                  <div className="divide-y divide-nardo-100 border border-nardo-100 rounded-md">
                    {list.map((p) => (
                      <div key={p.id} className="px-3 py-2 flex items-center justify-between gap-2" data-testid="exp-payment-item">
                        <div className="min-w-0">
                          <div className="text-sm text-navy">{p.concept} · <span className="text-emerald-700 font-mono">{money(p.net_amount)}</span></div>
                          <div className="text-[10px] text-nardo-500">{p.folio} · {p.method}</div>
                        </div>
                        <div className="flex items-center gap-1 shrink-0">
                          <button onClick={() => downloadReceipt(p)} className="p-1.5 text-nardo-500 hover:text-navy hover:bg-nardo-50 rounded" title="PDF"><Download className="w-4 h-4" /></button>
                          <button onClick={() => sendWa(p)} className="p-1.5 text-emerald-600 hover:bg-emerald-50 rounded" title="WhatsApp"><MessageCircle className="w-4 h-4" /></button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>

      {/* Documentos del expediente */}
      <Card accent="bg-violet-500" testid="expediente-files-card">
        <div className="mb-4">
          <h3 className="font-display font-semibold text-lg text-navy flex items-center gap-2"><Paperclip className="w-4 h-4 text-violet-500" /> Documentos del expediente</h3>
          <p className="text-[11px] text-nardo-400 mt-1">Justificantes, comprobantes, fotos y archivos del alumno (máx. 15 MB c/u).</p>
        </div>
        <div className="flex flex-wrap items-center gap-2 mb-4">
          <select data-testid="exp-file-category" value={fileCategory} onChange={(e) => setFileCategory(e.target.value)} className="border border-nardo-200 rounded-md px-2 py-2 text-sm text-navy bg-white outline-none focus:border-navy">
            {FILE_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <button type="button" onClick={() => expFileRef.current?.click()} disabled={uploadingFile} data-testid="exp-add-file" className="inline-flex items-center gap-1.5 bg-navy hover:bg-navy-light text-white px-4 py-2 rounded-md text-sm font-medium transition-colors disabled:opacity-50"><Upload className="w-4 h-4" /> {uploadingFile ? "Subiendo…" : "Agregar archivo"}</button>
          <button type="button" onClick={() => expCameraRef.current?.click()} disabled={uploadingFile} data-testid="exp-take-photo" className="inline-flex items-center gap-1.5 border border-navy/30 text-navy hover:bg-navy hover:text-white px-4 py-2 rounded-md text-sm font-medium transition-colors disabled:opacity-50"><Camera className="w-4 h-4" /> Tomar foto</button>
          <input ref={expFileRef} type="file" accept="image/*,application/pdf" multiple className="hidden" onChange={(e) => uploadExpFiles(e.target.files)} />
          <input ref={expCameraRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => uploadExpFiles(e.target.files)} />
        </div>
        {expFiles.length === 0 ? <p className="text-sm text-nardo-500">Aún no hay documentos en el expediente.</p> : (
          <div className="space-y-5" data-testid="exp-files-list">
            {filesByCategory.map(([cat, list]) => (
              <div key={cat} data-testid={`exp-folder-${cat}`}>
                <div className="flex items-center gap-2 mb-2">
                  <Folder className="w-4 h-4 text-violet-500" />
                  <span className="text-[11px] uppercase tracking-[0.2em] text-navy font-semibold">{cat}</span>
                  <span className="text-[10px] text-nardo-400">· {list.length}</span>
                  <span className="flex-1 h-px bg-nardo-100" />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {list.map((f) => {
                    const isImg = (f.content_type || "").startsWith("image/");
                    const fileUrl = `${API}/files/${f.file_path}`;
                    return (
                      <div key={f.id} className="border border-nardo-100 rounded-md overflow-hidden" data-testid={`exp-file-${f.id}`}>
                        {isImg ? (
                          <a href={fileUrl} target="_blank" rel="noreferrer"><img src={fileUrl} alt={f.title} className="w-full h-28 object-cover bg-nardo-50" /></a>
                        ) : (
                          <a href={fileUrl} target="_blank" rel="noreferrer" className="flex items-center justify-center h-28 bg-nardo-50"><FileText className="w-10 h-10 text-nardo-400" /></a>
                        )}
                        <div className="p-2.5">
                          <div className="text-xs font-medium text-navy truncate" title={f.title}>{f.title}</div>
                          <div className="text-[10px] text-nardo-400 mt-0.5">
                            {f.source === "justificante" ? "Justificante" : "Documento"}{f.date ? ` · ${f.date}` : ""}
                          </div>
                          <div className="flex items-center gap-2 mt-2">
                            <a href={fileUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[11px] text-navy hover:underline" data-testid={`exp-file-open-${f.id}`}><ExternalLink className="w-3 h-3" /> Abrir</a>
                            <button onClick={() => deleteExpFile(f.id)} className="inline-flex items-center gap-1 text-[11px] text-nardo-400 hover:text-destructive ml-auto" data-testid={`exp-file-delete-${f.id}`}><Trash2 className="w-3 h-3" /> Quitar</button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      <Card accent="bg-red-500" testid="expediente-incidents-card">
        <div className="flex items-center gap-2 mb-1">
          <FileText className="w-4 h-4 text-red-500" />
          <h2 className="font-display font-semibold text-lg text-navy">Bitácora Digital</h2>
          <span className="text-[10px] text-nardo-400">· {incidents.length}</span>
          <button onClick={() => downloadPdfFile("/direccion/incidents/pdf", { params: { student_id: id, label: `Expediente · ${student?.full_name || "alumno"}` }, filename: `incidencias_${student?.full_name || "alumno"}.pdf` })} disabled={incidents.length === 0} data-testid="exp-inc-pdf" className="ml-auto text-[11px] inline-flex items-center gap-1 text-navy border border-navy/30 hover:bg-navy hover:text-white rounded-md px-2.5 py-1.5 disabled:opacity-40 transition-colors"><Download className="w-3 h-3" /> PDF</button>
        </div>
        <p className="text-xs text-nardo-500 mb-4">Bitácora del alumno registrada por docentes y validada por Dirección, organizada por día.</p>
        {incidents.length === 0 ? <p className="text-sm text-nardo-500">Sin registros en la bitácora.</p> : (
          <div className="space-y-2" data-testid="exp-incidents-list">
            {incidents.map((it) => {
              const cls = { academico: "bg-amber-500", conductual: "bg-indigo-500", socioemocional: "bg-teal-500", conducta: "bg-red-500", reconocimiento: "bg-emerald-500", academica: "bg-amber-500", salud: "bg-sky-500", otro: "bg-slate-500" }[it.type] || "bg-slate-500";
              const lbl = { academico: "Académico", conductual: "Conductual", socioemocional: "Socio emocional", conducta: "Conducta negativa", reconocimiento: "Reconocimiento positivo", academica: "Académica", salud: "Salud", otro: "Otro" }[it.type] || it.type;
              return (
                <div key={it.id} className="flex items-start gap-3 border border-nardo-100 rounded-md p-3" data-testid={`exp-inc-${it.id}`}>
                  <span className="text-[10px] text-nardo-400 font-mono shrink-0 mt-0.5 w-20">{(it.created_at || "").slice(0, 10)}</span>
                  <span className={`shrink-0 text-[10px] font-semibold text-white px-2 py-0.5 rounded-full ${cls}`}>{lbl}</span>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm text-nardo-700">{it.description}</div>
                    <div className="text-[10px] text-nardo-400 mt-0.5">Nivel {it.level} · Por {it.created_by_name} · {it.status === "atendida" ? `Atendida por ${it.attended_by || "Dirección"}` : "Pendiente"}{it.photo_token ? " · " : ""}{it.photo_token && <a href={`${API}/incident-photo/${it.photo_token}`} target="_blank" rel="noreferrer" className="text-navy underline">foto</a>}</div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Card>
    </div>
  );
}
